import React from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import AttachmentIcon from '@atlaskit/icon/core/attachment';
import AudioIcon from '@atlaskit/icon/core/audio';
import ImageIcon from '@atlaskit/icon/core/image';
import PageIcon from '@atlaskit/icon/core/page';
import VideoIcon from '@atlaskit/icon/core/video';
import { fg } from '@atlaskit/platform-feature-flags/fg';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { Anchor, Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	ProductKeys3P,
	type ProductKeys3P as ProductKeys3PType,
} from '@atlassian/rovo-connectors-config/types';

import { LastModified } from '../../../../../../../../../common/ui/last-modified';
import { enrichExcerpt } from '../../../../../../../../../common/utils/search-result-utils';

import type { attachmentItems_AtlassianSearchPage_DropdownItems$key } from './__generated__/attachmentItems_AtlassianSearchPage_DropdownItems.graphql';
import { i18n } from './messages';

const dropdownItemStyles = xcss({
	display: 'flex',
	columnGap: 'space.100',
	paddingBlock: 'space.050',
	marginTop: 'space.100',
});

const attachmentStyles = xcss({
	color: 'color.link',
	textDecoration: 'none',
	':hover': { color: 'color.link', textDecoration: 'underline' },
	':active': { textDecoration: 'none' },
	':visited': { color: 'color.link.visited' },
	// NOTE: this selector is preventing migration to @atlaskit/css as it's not officially supported.
	// Context: https://atlassian.slack.com/archives/CFJ9DU39U/p1754279429281269
	':active:visited': { color: 'color.link.visited.pressed' },
});

const graphQlFileTypeToConsolidatedFileType = (fileType: string | undefined | null) => {
	if (!fileType) {
		return 'attachment';
	}

	switch (fileType) {
		case 'image':
			return 'image';
		case 'audio':
			return 'audio';
		case 'video':
			return 'video';
		default:
			return 'attachment';
	}
};

const fileTypeToIcon = (
	fileType: string | undefined | null,
	graphQlFileType: string | undefined | null,
) => {
	if (!fileType) {
		return AttachmentIcon;
	}

	if (graphQlFileType && graphQlFileType === 'pdf') {
		return PageIcon;
	}

	switch (fileType) {
		case 'image':
			return ImageIcon;
		case 'audio':
			return AudioIcon;
		case 'video':
			return VideoIcon;
		default:
			return AttachmentIcon;
	}
};

const fileTypeToLabel = (
	fileType: string | undefined | null,
	graphQlFileType: string | undefined | null,
) => {
	if (!fileType) {
		return i18n.attachmentLabel;
	}

	if (graphQlFileType && graphQlFileType === 'pdf') {
		return i18n.pdfLabel;
	}

	switch (fileType) {
		case 'image':
			return i18n.imageLabel;
		case 'audio':
			return i18n.audioLabel;
		case 'video':
			return i18n.videoLabel;
		default:
			return i18n.attachmentLabel;
	}
};
const mimeTypeToIcon = (mimeType: string | undefined | null) => {
	if (!mimeType) {
		return AttachmentIcon;
	}

	const [type, specificType] = mimeType.split('/');
	if (specificType === 'pdf') {
		return PageIcon;
	}

	switch (type) {
		case 'video':
			return VideoIcon;
		case 'image':
			return ImageIcon;
		case 'audio':
			return AudioIcon;
		default:
			return AttachmentIcon;
	}
};

const mimeTypeToLabel = (mimeType: string | undefined | null) => {
	if (!mimeType) {
		return i18n.attachmentLabel;
	}

	const [type, specificType] = mimeType.split('/');
	if (specificType === 'pdf') {
		return i18n.pdfLabel;
	}

	switch (type) {
		case 'video':
			return i18n.videoLabel;
		case 'image':
			return i18n.imageLabel;
		case 'audio':
			return i18n.audioLabel;
		default:
			return i18n.attachmentLabel;
	}
};

export const DropdownItems = ({
	node,
	product,
}: {
	node: attachmentItems_AtlassianSearchPage_DropdownItems$key;
	product?: ProductKeys3PType;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const forceUnclickableForTeams =
		fg('search-page-unclickable-attachments-teams') && product === ProductKeys3P.Teams;

	const data = useFragment(
		graphql`
			fragment attachmentItems_AtlassianSearchPage_DropdownItems on SearchResultGraphDocument {
				lastModifiedDate
				attachments {
					title
					fileType
					url
				}
				entity {
					... on ExternalMessage {
						attachments {
							title
							mimeType
							url
							thumbnailUrl
						}
					}
				}
			}
		`,
		node,
	);

	// Prefer top-level GraphQL attachments when available,
	// otherwise fall back to entity attachments
	const useGraphqlAttachments = (data.attachments ?? []).length > 0;

	return (
		<Box>
			{useGraphqlAttachments
				? data.attachments?.map((attachment) => {
						if (!attachment) {
							return null;
						}

						const { title, url, fileType } = attachment;
						const consolidatedFileType = graphQlFileTypeToConsolidatedFileType(fileType);
						const FileTypeIcon = fileTypeToIcon(consolidatedFileType, fileType);
						const truncatedUrl = url?.replace(/\/download\//g, '/');
						const viewUrl = forceUnclickableForTeams ? undefined : truncatedUrl;

						return (
							<Stack xcss={dropdownItemStyles} space="space.050" key={url}>
								<Inline alignBlock="center" space="space.025">
									<FileTypeIcon
										label="icon"
										size="medium"
										color={token('color.icon.accent.blue')}
									/>
									<Text size="medium" color="color.link" weight="medium">
										{viewUrl ? (
											<Anchor
												xcss={attachmentStyles}
												href={viewUrl}
												rel={'noreferrer'}
												target="_blank"
											>
												{enrichExcerpt(title)}
											</Anchor>
										) : (
											enrichExcerpt(title)
										)}
									</Text>
								</Inline>
								<Inline
									separator={<Text size="small">•</Text>}
									space="space.050"
									alignBlock="center"
								>
									<Text size="small">
										{formatMessage(fileTypeToLabel(consolidatedFileType, fileType))}
									</Text>
									<Text size="small" color="color.text.subtle">
										<LastModified
											lastModified={data.lastModifiedDate}
											message={i18n.attachedDateLabel}
										/>
									</Text>
								</Inline>
							</Stack>
						);
					})
				: data.entity?.attachments?.map((attachment) => {
						if (!attachment) {
							return null;
						}
						const { title, mimeType, url, thumbnailUrl } = attachment;
						const MimeTypeIcon = mimeTypeToIcon(mimeType);
						const truncatedUrl = url?.replace(/\/download\//g, '/');
						const viewUrl = forceUnclickableForTeams ? undefined : (truncatedUrl ?? thumbnailUrl);

						return (
							<Stack xcss={dropdownItemStyles} space="space.050" key={url}>
								<Inline alignBlock="center" space="space.025">
									<MimeTypeIcon
										label="icon"
										size="medium"
										color={token('color.icon.accent.blue')}
									/>
									<Text size="medium" color="color.link" weight="medium">
										{viewUrl ? (
											<Anchor
												xcss={attachmentStyles}
												href={viewUrl}
												rel={'noreferrer'}
												target="_blank"
											>
												{enrichExcerpt(title)}
											</Anchor>
										) : (
											enrichExcerpt(title)
										)}
									</Text>
								</Inline>
								<Inline
									separator={<Text size="small">•</Text>}
									space="space.050"
									alignBlock="center"
								>
									<Text size="small">{formatMessage(mimeTypeToLabel(mimeType))}</Text>
									<Text size="small" color="color.text.subtle">
										<LastModified
											lastModified={data.lastModifiedDate}
											message={i18n.attachedDateLabel}
										/>
									</Text>
								</Inline>
							</Stack>
						);
					})}
		</Box>
	);
};
